/**
 * Phase 3.66 — Werbung & Reichweite Akkordeon im Inserat-Formular.
 *
 * 3 Sub-Blöcke:
 *   1) Top-Inserat (Boost via €Punkte direkt beim Veröffentlichen)
 *   2) Börsen-Export (aus dem Händlerprofil aktivierte Börsen wählen)
 *   3) Social-Media (aus dem Händlerprofil aktivierte Kanäle wählen)
 */
import { useEffect, useState } from "react";
import { Megaphone, Star, ExternalLink, Share2, Loader2, Users, RefreshCw, MessageSquare, KeyRound, Handshake, Truck, Upload, FileText, X, Shield, QrCode, AlertCircle, Zap, PhoneMissed } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const EXCHANGES = [
    { key: "mobile", label: "mobile.de", flag: "export_mobile" },
    { key: "autoscout", label: "AutoScout24", flag: "export_autoscout" },
    { key: "kleinanzeigen", label: "Kleinanzeigen", flag: "export_kleinanzeigen" },
    { key: "ebay_fahrzeugmarkt", label: "eBay Fahrzeugmarkt", flag: "export_ebay_fahrzeugmarkt" },
    { key: "google", label: "Google Fahrzeuganzeigen", flag: "export_google" },
];
const SOCIALS = [
    { key: "whatsapp", label: "WhatsApp", flag: "social_whatsapp_enabled" },
    { key: "instagram", label: "Instagram", flag: "social_instagram_enabled" },
    { key: "tiktok", label: "TikTok", flag: "social_tiktok_enabled" },
    { key: "facebook", label: "Facebook", flag: "social_facebook_enabled" },
    { key: "youtube", label: "YouTube", flag: "social_youtube_enabled" },
    { key: "x", label: "X (Twitter)", flag: "social_x_enabled" },
];

export default function AdReachSection({ form, update, user, listingId }) {
    const { t } = useTranslation();
    const [boostInfo, setBoostInfo] = useState(null);
    const [watermarking, setWatermarking] = useState(false);
    const isDealer = user?.role === "b2b";
    const enabledExchanges = EXCHANGES.filter((x) => !!user?.[x.flag]);
    const enabledSocials = SOCIALS.filter((s) => user?.social_media_enabled && !!user?.[s.flag]);

    useEffect(() => {
        if (!isDealer) return;
        api.get("/listings/boost/info").then((r) => setBoostInfo(r.data)).catch(() => {});
    }, [isDealer]);

    const toggleArr = (arr, key) => {
        const a = Array.isArray(arr) ? [...arr] : [];
        const idx = a.indexOf(key);
        if (idx >= 0) a.splice(idx, 1); else a.push(key);
        return a;
    };

    // Feb-2026 (User-Wunsch 24 May Iter 16) — Bild-Wasserzeichen mit
    // Händlername + Angebotsnummer + QR-Code. Manuelle Anwendung möglich
    // (nur für bereits gespeicherte Inserate). Bei neuen Inseraten wirkt
    // der Toggle als „nach Veröffentlichung anwenden" — die finale Speicher-
    // logik kümmert sich beim Submit.
    const applyWatermark = async () => {
        if (!listingId) {
            toast.error("Bitte das Inserat zuerst speichern oder als Entwurf anlegen.");
            return;
        }
        setWatermarking(true);
        try {
            const { data } = await api.post(`/listings/${listingId}/apply-watermark`);
            if (!data?.ok) {
                toast.error("Wasserzeichen konnte nicht angewendet werden — keine eigenen Bilder gefunden.");
                return;
            }
            update("images", data.images);
            update("images_watermarked", true);
            toast.success(`${data.count} Bild(er) mit Wasserzeichen + QR-Code geschützt — Nr. ${data.internal_number}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Wasserzeichen fehlgeschlagen");
        } finally {
            setWatermarking(false);
        }
    };

    const removeWatermark = async () => {
        if (!listingId) return;
        setWatermarking(true);
        try {
            const { data } = await api.post(`/listings/${listingId}/remove-watermark`);
            if (!data?.ok) {
                toast.error("Keine Original-Bilder hinterlegt.");
                return;
            }
            update("images", data.images);
            update("images_watermarked", false);
            toast.success("Original-Bilder wiederhergestellt.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rückgängig fehlgeschlagen");
        } finally {
            setWatermarking(false);
        }
    };

    if (!isDealer) return null;

    return (
        <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-6" data-testid="ad-reach-section">
            {/* Feb-2026 (User-Wunsch 24 May Iter 14): doppelte „Werbung & Reichweite"-
                Überschrift entfernt — Outer SectionToggle trägt den Titel bereits. */}

            {/* Mai-2026 (User-Wunsch 21 May Iteration 2):
                Sichtbarkeit + Inzahlungnahme ganz nach oben — VOR den Direkt-Chat-Toggle. */}

            {/* Sichtbarkeit / Käufergruppen */}
            <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-visibility-block">
                <div className="font-bold text-[#111827] flex items-center gap-1.5 mb-2">
                    <Users className="w-4 h-4 text-gray-700" />
                    Sichtbarkeit — Wer darf mein Fahrzeugbestand / Inserat sehen?
                </div>
                <p className="text-xs text-gray-600 mb-2">
                    Schränken Sie das Publikum ein. Mindestens eine Käufergruppe muss aktiv bleiben.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="ad-reach-visibility-b2c-wrap">
                        <input
                            type="checkbox"
                            checked={form.visibility_b2c !== false}
                            onChange={(e) => {
                                const next = e.target.checked;
                                if (!next && form.visibility_b2b === false) return;  // muss ≥1 aktiv
                                update("visibility_b2c", next);
                            }}
                            className="mt-0.5 w-4 h-4 accent-[#111827]"
                            data-testid="ad-reach-visibility-b2c"
                        />
                        <span>
                            <span className="font-medium">B2C — private Käufer</span>
                            <span className="block text-[11px] text-gray-500">Endkunden sehen das Inserat in Suche, Detail-Page und Favoriten.</span>
                        </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="ad-reach-visibility-b2b-wrap">
                        <input
                            type="checkbox"
                            checked={form.visibility_b2b !== false}
                            onChange={(e) => {
                                const next = e.target.checked;
                                if (!next && form.visibility_b2c === false) return;
                                update("visibility_b2b", next);
                            }}
                            className="mt-0.5 w-4 h-4 accent-[#111827]"
                            data-testid="ad-reach-visibility-b2b"
                        />
                        <span>
                            <span className="font-medium">B2B — gewerbliche Käufer</span>
                            <span className="block text-[11px] text-gray-500">Andere Händler sehen das Inserat (z. B. für Aufkauf, Großhandel).</span>
                        </span>
                    </label>
                </div>
            </div>

            {/* Feb-2026 (User-Wunsch) — „Preisvorschläge erlauben" pro Inserat
                wieder eingeführt (konsistent mit Inzahlungnahme/Chat/Probefahrt).
                Wird nur angezeigt, wenn der Händler Preisvorschläge global im
                Profil („Marktplatzeinstellungen") aktiviert hat UND es sich nicht
                um eine Auktion handelt. Vorbelegung erbt die globale Einstellung,
                ist aber pro Inserat überschreibbar. */}
            {!!user?.allow_offers && form.sale_type !== "auction" && (
                <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-offers-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.allow_offers}
                            onChange={(e) => update("allow_offers", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="allow-offers-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <Handshake className="w-4 h-4 text-[#FFCC00]" />
                                {t("create_listing.allow_offers_title")}
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                {t("create_listing.allow_offers_hint")}
                            </div>
                            {form.allow_offers && (
                                <div className="mt-3 max-w-xs">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                        {t("create_listing.min_offer_label")}
                                    </Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={form.min_offer_amount}
                                        onChange={(e) => update("min_offer_amount", e.target.value)}
                                        placeholder={t("create_listing.min_offer_placeholder")}
                                        className="mt-1.5 rounded-sm font-mono-num"
                                        data-testid="min-offer-amount-input"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">{t("create_listing.min_offer_hint")}</p>
                                    {(() => {
                                        const abs = Number(form.min_offer_amount);
                                        const pct = Number(user?.min_offer_pct);
                                        const price = Number(form.price);
                                        if (!Number.isFinite(abs) || abs <= 0) return null;
                                        if (!Number.isFinite(pct) || pct <= 0) return null;
                                        if (!Number.isFinite(price) || price <= 0) return null;
                                        const pctCutoff = Math.round((price * pct) / 100);
                                        if (abs <= pctCutoff) return null;
                                        return (
                                            <div
                                                className="mt-2 flex items-start gap-1.5 rounded-sm bg-[#FEF2F2] border border-[#FECACA] px-3 py-2"
                                                data-testid="min-offer-amount-stricter-warning"
                                            >
                                                <AlertCircle className="w-3.5 h-3.5 text-[#B91C1C] mt-0.5 shrink-0" />
                                                <span className="text-[11px] text-[#991B1B] leading-snug">
                                                    Ihr €-Wert <strong>{abs.toLocaleString("de-DE")} €</strong> ist strenger als der
                                                    globale {pct} %-Wert (<strong>{pctCutoff.toLocaleString("de-DE")} €</strong> bei {price.toLocaleString("de-DE")} € Preis) —
                                                    es greift der höhere €-Wert. Mehr Vorschläge werden automatisch abgelehnt.
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            {form.allow_offers && !!user?.auto_counter_offers && (
                                <div className="mt-3 max-w-xs" data-testid="auto-counter-amount-block">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                                        <Zap className="w-3 h-3 text-[#FFCC00]" /> Gegenvorschlag-Preis (€)
                                    </Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={form.auto_counter_amount}
                                        onChange={(e) => update("auto_counter_amount", e.target.value)}
                                        placeholder="leer = Mindestpreis als Gegenvorschlag"
                                        className="mt-1.5 rounded-sm font-mono-num"
                                        data-testid="auto-counter-amount-input"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Bei Lowball-Vorschlägen unter dem Mindestpreis wird automatisch mit diesem Preis gekontert.
                                        Leer = es wird mit dem Mindestpreis gekontert.
                                    </p>
                                    {(() => {
                                        const cnt = Number(form.auto_counter_amount);
                                        const floor = Number(form.min_offer_amount);
                                        if (!Number.isFinite(cnt) || cnt <= 0) return null;
                                        if (!Number.isFinite(floor) || floor <= 0) return null;
                                        if (cnt >= floor) return null;
                                        return (
                                            <div
                                                className="mt-2 flex items-start gap-1.5 rounded-sm bg-[#FEF2F2] border border-[#FECACA] px-3 py-2"
                                                data-testid="auto-counter-below-floor-warning"
                                            >
                                                <AlertCircle className="w-3.5 h-3.5 text-[#B91C1C] mt-0.5 shrink-0" />
                                                <span className="text-[11px] text-[#991B1B] leading-snug">
                                                    Gegenvorschlag <strong>{cnt.toLocaleString("de-DE")} €</strong> liegt unter Ihrem
                                                    Mindestpreis <strong>{floor.toLocaleString("de-DE")} €</strong> — es wird stattdessen
                                                    mit dem Mindestpreis gekontert.
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            {form.allow_offers && Number(user?.min_offer_pct) > 0 && (
                                <div
                                    className="mt-3 flex items-start gap-1.5 rounded-sm bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2"
                                    data-testid="ad-reach-offers-global-pct-hint"
                                >
                                    <Shield className="w-3.5 h-3.5 text-[#B45309] mt-0.5 shrink-0" />
                                    <span className="text-[11px] text-[#92400E] leading-snug">
                                        {(() => {
                                            const pct = Number(user.min_offer_pct);
                                            const price = Number(form.price);
                                            if (Number.isFinite(price) && price > 0) {
                                                const cutoff = Math.round((price * pct) / 100);
                                                return (
                                                    <>
                                                        Global: Bei Ihrem Preis <strong>{price.toLocaleString("de-DE")} €</strong> werden
                                                        Vorschläge unter <strong>{cutoff.toLocaleString("de-DE")} €</strong> (= {pct} %)
                                                        zusätzlich automatisch abgelehnt — aus Ihren Marktplatzeinstellungen.
                                                        Greift ergänzend zum €-Schwellwert oben.
                                                    </>
                                                );
                                            }
                                            return (
                                                <>
                                                    Global: Vorschläge unter <strong>{pct} %</strong> des Festpreises
                                                    werden zusätzlich automatisch abgelehnt (aus Ihren Marktplatzeinstellungen).
                                                    Greift ergänzend zum €-Schwellwert oben.
                                                </>
                                            );
                                        })()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </label>
                </div>
            )}

            {user?.instant_buy_enabled && form.sale_type !== "auction" && (
                <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-instant-buy-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.instant_buy_enabled}
                            onChange={(e) => update("instant_buy_enabled", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="instant-buy-listing-checkbox"
                        />
                        <div>
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <Zap className="w-4 h-4 text-[#FFCC00]" />
                                Sofortkauf zulassen
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Zeigt einen „Jetzt verbindlich kaufen"-Button — Interessenten kaufen ohne Verhandlung direkt
                                (Reservierung → Kaufvertrag). Vorbelegung aus Ihren Marktplatzeinstellungen, hier pro Inserat überschreibbar.
                            </div>
                        </div>
                    </label>
                </div>
            )}

            {/* Inzahlungnahme & Direkt-Chat sind seit Feb-2026 (User-Wunsch 24 May
                Iteration 4) wieder hier verankert — fachlich passen sie besser
                in „Werbung & Reichweite", weil sie Marketing-/Reichweiten-
                Mechaniken sind. Beide Toggles werden nur angezeigt, wenn der
                Händler sie im Profil unter „Marktplatzeinstellungen" aktiviert hat. */}
            {user?.allow_tradein && form.sale_type !== "auction" && (
                <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-tradein-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.allow_tradein}
                            onChange={(e) => update("allow_tradein", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="allow-tradein-checkbox"
                        />
                        <div>
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <RefreshCw className="w-4 h-4 text-[#FFCC00]" />
                                In Zahlung nahme akzeptieren
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Interessenten können ihr Gebrauchtfahrzeug in Zahlung anbieten und die Differenz als Anzahlung leisten — erhöht Reichweite und Conversion-Wahrscheinlichkeit.
                            </div>
                        </div>
                    </label>
                </div>
            )}
            {user?.allow_chat !== false && (
                <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-chat-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.allow_chat}
                            onChange={(e) => update("allow_chat", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="allow-chat-checkbox"
                        />
                        <div>
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <MessageSquare className="w-4 h-4 text-[#FFCC00]" />
                                Direkt-Chat aktivieren
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Interessenten können Sie über das Inserat anschreiben. Bei Deaktivierung wird kein Chat-Formular angezeigt — Anfragen kommen dann nur über Telefon.
                            </div>
                        </div>
                    </label>
                </div>
            )}
            {!!user?.missed_call_alerts_enabled && (
                <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-missed-call-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.missed_call_alerts_enabled !== false}
                            onChange={(e) => update("missed_call_alerts_enabled", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="missed-call-listing-checkbox"
                        />
                        <div>
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <PhoneMissed className="w-4 h-4 text-[#FFCC00]" />
                                Bei verpassten Anrufen benachrichtigen
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Ruft ein Interessent zu diesem Inserat an und Sie nehmen nicht ab, werden Sie benachrichtigt. Zusätzlich erscheint im Inserat ein „Rückruf anfordern"-Button, über den Interessenten direkt einen Rückruf erbitten können (Dashboard, E-Mail, SMS — je nach Profil-Einstellung).
                            </div>
                        </div>
                    </label>
                </div>
            )}

            {/* Feb-2026 (User-Wunsch 24 May Iteration 6) — Probefahrten anbieten
                gehört fachlich in „Werbung & Reichweite", weil es eine Lead-
                Mechanik ist (Conversion vor Ort). Spiegelung mit dem Toggle
                im Service-Block in ExtraTechnicalSection — beide editieren
                dasselbe Feld `allow_test_ride`. */}
            {!!user?.allow_test_ride && (
                <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-test-ride-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.allow_test_ride}
                            onChange={(e) => update("allow_test_ride", e.target.checked)}
                            className="mt-1 w-5 h-5 accent-[#0EA5E9]"
                            data-testid="ad-reach-allow-test-ride-checkbox"
                        />
                        <div>
                            <div className="font-bold text-[#111827] flex items-center gap-1.5">
                                <KeyRound className="w-4 h-4 text-[#0EA5E9]" />
                                Probefahrten anbieten
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Interessenten können direkt aus dem Inserat eine Probefahrt-Anfrage stellen — erhöht die Conversion vor Ort.
                            </div>
                        </div>
                    </label>
                </div>
            )}

            {/* Feb-2026 (User-Wunsch 24 May Iter 14): Sofortmitnahme / Abholung
                + zugehörige Unter-Optionen + Bundesweite Lieferung — aus
                ExtraTechnicalSection (Service-Block) hierhin verschoben, weil
                es Reichweite- und Conversion-Mechaniken sind. */}
            <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-pickup-block">
                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.pickup_available}
                        onChange={(e) => update("pickup_available", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#111827]"
                        data-testid="ad-reach-pickup-available"
                    />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827] flex items-center gap-1.5">
                            <Truck className="w-4 h-4 text-gray-700" />
                            Sofortmitnahme / Abholung möglich
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            Käufer kann das Fahrzeug zeitnah übernehmen.
                        </div>
                        {form.pickup_available && (
                            <div className="mt-3 pl-1 space-y-2" data-testid="ad-reach-pickup-options">
                                <label className="flex items-start gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!form.pickup_with_registration}
                                        onChange={(e) => update("pickup_with_registration", e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-[#111827]"
                                        data-testid="ad-reach-pickup-with-registration"
                                    />
                                    <span>
                                        <span className="font-medium">Fahrzeug ist noch zugelassen</span>
                                        <span className="block text-[11px] text-gray-500">Gültige eVB-Nummer / Versicherungsbestätigung mitbringen — gültig ab Übernahmedatum.</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!form.pickup_unregistered_transport}
                                        onChange={(e) => update("pickup_unregistered_transport", e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-[#111827]"
                                        data-testid="ad-reach-pickup-unregistered-transport"
                                    />
                                    <span>
                                        <span className="font-medium">Auslieferungsfertig, nicht zugelassen</span>
                                        <span className="block text-[11px] text-gray-500">Muss transportiert werden (kein eigenes Fahren ohne Zulassung).</span>
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                </label>
            </div>

            <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-delivery-block">
                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.delivery_nationwide}
                        onChange={(e) => update("delivery_nationwide", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#111827]"
                        data-testid="ad-reach-delivery-nationwide"
                    />
                    <div>
                        <div className="font-bold text-[#111827] flex items-center gap-1.5">
                            <Truck className="w-4 h-4 text-gray-700" />
                            Bundesweite Lieferung möglich
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            Angebot bei registrierten Dienstleistern einholen — Kosten trägt der Käufer.
                        </div>
                    </div>
                </label>
            </div>

            {/* 1) Top-Inserat */}
            <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-top-block">
                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.boost_on_publish}
                        onChange={(e) => update("boost_on_publish", e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#DC2626]"
                        data-testid="ad-reach-boost-checkbox"
                    />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827] flex items-center gap-1.5">
                            <Star className="w-4 h-4 text-[#DC2626]" fill="currentColor" />
                            Top-Inserat sofort buchen
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            {boostInfo ? (
                                <>
                                    {boostInfo.cost_points} €Punkte (~{Number(boostInfo.cost_eur).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}) für {boostInfo.duration_days} Tage Top-Platzierung.
                                    Verfügbar: <strong className={boostInfo.can_afford ? "text-emerald-700" : "text-rose-700"}>{boostInfo.available_points} €Punkte</strong>.
                                    {!boostInfo.can_afford && form.boost_on_publish && (
                                        <span className="block mt-1 text-rose-700">⚠ Guthaben reicht nicht — der Boost wird übersprungen.</span>
                                    )}
                                </>
                            ) : (
                                <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Lade Konditionen…</span>
                            )}
                        </div>
                    </div>
                </label>
            </div>

            {/* 2) Börsen-Export */}
            <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-exchanges-block">
                <div className="font-bold text-[#111827] flex items-center gap-1.5 mb-2">
                    <ExternalLink className="w-4 h-4 text-gray-700" />
                    Börsen-Export
                </div>
                {enabledExchanges.length === 0 ? (
                    <p className="text-xs text-gray-500">
                        Keine Börsen-Exporte im Händlerprofil aktiviert. Konfigurieren Sie diese unter <em>Marktplatzeinstellungen → Börsenexporte</em>.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {enabledExchanges.map((ex) => {
                            const useZwr = !!user?.[`${ex.flag}_use_zweiradhaendler`];
                            const useOwn = !!user?.[`${ex.flag}_use_own`];
                            const showRatings = !!user?.[`${ex.flag}_show_portal_ratings`];
                            return (
                                <label key={ex.key} className="flex items-start gap-2 cursor-pointer text-sm border border-gray-100 rounded-sm p-2.5 hover:border-gray-300 transition-colors" data-testid={`ad-reach-exchange-${ex.key}-wrap`}>
                                    <input
                                        type="checkbox"
                                        checked={(form.publish_to_exchanges || []).includes(ex.key)}
                                        onChange={() => update("publish_to_exchanges", toggleArr(form.publish_to_exchanges, ex.key))}
                                        className="w-4 h-4 accent-[#111827] mt-0.5"
                                        data-testid={`ad-reach-exchange-${ex.key}`}
                                    />
                                    <span className="flex-1 min-w-0">
                                        <span className="font-semibold block">{ex.label}</span>
                                        <span className="flex flex-wrap gap-1 mt-1" data-testid={`ad-reach-exchange-${ex.key}-mode`}>
                                            {useZwr && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#FFCC00]/30 text-[#111827] border border-[#FFCC00] px-1.5 py-0.5 rounded-sm">
                                                    Über „Die Zweiradhändler"
                                                </span>
                                            )}
                                            {useOwn && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700 border border-gray-300 px-1.5 py-0.5 rounded-sm">
                                                    Eigener Händler-Zugang
                                                </span>
                                            )}
                                            {!useZwr && !useOwn && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                                                    Kein Zugang gewählt
                                                </span>
                                            )}
                                            {showRatings && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm" data-testid={`ad-reach-exchange-${ex.key}-ratings`}>
                                                    Händlerbewertungen an
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 3) Social-Media */}
            <div className="border border-gray-200 rounded-sm p-4 mb-3" data-testid="ad-reach-social-block">
                <div className="font-bold text-[#111827] flex items-center gap-1.5 mb-2">
                    <Share2 className="w-4 h-4 text-gray-700" />
                    Social-Media-Posting
                </div>
                {enabledSocials.length === 0 ? (
                    <p className="text-xs text-gray-500">
                        Keine Social-Media-Kanäle im Händlerprofil aktiviert. Konfigurieren Sie diese unter <em>Marktplatzeinstellungen → Social Media</em>.
                    </p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {enabledSocials.map((s) => (
                            <label key={s.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`ad-reach-social-${s.key}-wrap`}>
                                <input
                                    type="checkbox"
                                    checked={(form.publish_to_social || []).includes(s.key)}
                                    onChange={() => update("publish_to_social", toggleArr(form.publish_to_social, s.key))}
                                    className="w-4 h-4 accent-[#111827]"
                                    data-testid={`ad-reach-social-${s.key}`}
                                />
                                <span>{s.label}</span>
                            </label>
                        ))}
                    </div>
                )}
                <p className="text-[11px] text-gray-500 mt-2">
                    Hinweis: Die Versendung an Börsen und Social-Media erfolgt aktuell im Test-Modus (mocked). Live-Push wird mit Ihren API-Keys aktiviert.
                </p>
            </div>

            {/* Feb-2026 (User-Wunsch): Serviceheft / Verkaufsrechnung / weitere
                Dokumente wurden in die Disposition-Sektion verschoben
                (siehe ServiceDocsUpload via FooterActionsSection). */}

            {/* Feb-2026 (User-Wunsch 24 May Iter 16): Bild-Wasserzeichen +
                QR-Code. Brennt Händlername + Angebotsnummer + QR-Code unten
                rechts in alle Inseratsbilder ein → Schutz gegen Bild-Klau,
                Re-Use in Aggregatoren und Social-Media. Original wird im
                Backend in images_original archiviert (Revert möglich). */}
            <div className="border-2 border-[#FFCC00]/30 bg-yellow-50/40 rounded-sm p-4 mb-3" data-testid="ad-reach-watermark-block">
                <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-[#111827] mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="font-bold text-[#111827] flex items-center gap-1.5">
                            Bilder mit Wasserzeichen + QR-Code schützen
                            <QrCode className="w-3.5 h-3.5 text-gray-500" />
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">
                            Brennt deinen Händlernamen, die Angebotsnummer und einen QR-Code (Link zum Original-Inserat) unten rechts in jedes Bild. Schutz gegen Foto-Klau, der QR-Code führt jeden Betrachter zurück zu dir — auch wenn das Bild irgendwo anders re-postet wird.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={applyWatermark}
                                disabled={watermarking || !listingId}
                                className="inline-flex items-center gap-2 bg-[#111827] hover:bg-black text-[#FFCC00] text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="ad-reach-apply-watermark-btn"
                            >
                                {watermarking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                                {form.images_watermarked ? "Neu anwenden" : "Jetzt schützen"}
                            </button>
                            {form.images_watermarked && (
                                <button
                                    type="button"
                                    onClick={removeWatermark}
                                    disabled={watermarking}
                                    className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-[#111827] text-[#111827] text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm disabled:opacity-50"
                                    data-testid="ad-reach-remove-watermark-btn"
                                >
                                    <X className="w-3 h-3" /> Original wiederherstellen
                                </button>
                            )}
                            {!listingId && (
                                <span className="text-[11px] text-gray-500 italic self-center">
                                    Verfügbar nach erstem Speichern.
                                </span>
                            )}
                            {form.images_watermarked && (
                                <span
                                    className="inline-flex items-center gap-1 bg-[#FFCC00] text-[#111827] text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm"
                                    data-testid="ad-reach-watermark-status"
                                >
                                    <Shield className="w-3 h-3" /> Aktiv
                                </span>
                            )}
                        </div>
                        {/* Feb-2026 (User-Wunsch) — beim Veröffentlichen automatisch schützen (standardmäßig an) */}
                        <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid="ad-reach-auto-watermark-label">
                            <input
                                type="checkbox"
                                checked={form.auto_watermark_on_publish !== false}
                                onChange={(e) => update("auto_watermark_on_publish", e.target.checked)}
                                className="w-4 h-4 accent-[#111827] mt-0.5"
                                data-testid="ad-reach-auto-watermark-checkbox"
                            />
                            <span className="text-xs text-gray-700">
                                <span className="font-bold">Beim Veröffentlichen automatisch schützen</span> — Wasserzeichen + QR-Code werden beim Speichern/Veröffentlichen automatisch auf alle Bilder angewendet.
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Mai-2026 (User-Wunsch 21 May Iteration 2): Sichtbarkeits- und Inzahlungnahme-Blöcke
                wurden an den Anfang dieses Sektionsblocks (über Direkt-Chat) verschoben. */}
        </div>
    );
}
